import { type FileMode, isApiClientError, type V5Client } from "@mfa/v5-client";
import {
  type AdminDashboard,
  type AdminPlanSwitchResult,
  API_PREFIX,
  type ApiClientContext,
  type BlockDocument,
  type BlockOp,
  type BlocksSchemaResult,
  type CatalogSnapshot,
  CatalogSnapshotSchema,
  ExplorerListResponseSchema,
  type ExplorerUploadResponse,
  ExplorerUploadResponseSchema,
  IDEMPOTENCY_KEY_REUSED,
  memoryMountPath,
  type PlanId,
  type PreflightResult,
  type ProjectFileSaveMode,
  type ProjectRevision,
  type ProjectSidecar,
  type ProjectValidateInput,
  type ProjectValidation,
  type RunAttachmentsResponse,
  RunAttachmentsResponseSchema,
  type RunPreflight,
  type RunStartInput,
  type SandboxTimeResult,
  type StartRunCommand,
  type MemoryEntry as WireMemoryEntry,
} from "@mfa/v5-contracts";
import { type ReleaseWatch, releaseWatch } from "./release-watch.js";
import { RetryKeys } from "./retry-keys.js";
import type {
  ApiErrorInit,
  ApiKey,
  ApiKeyCreateInput,
  BillingAccountSnapshot,
  BillingCatalog,
  BillingSelectionStatus,
  BootstrapResponse,
  CatalogFacets,
  CatalogTemplate,
  ExplorerEntry,
  ExplorerListResponse,
  ExplorerPreview,
  ExplorerSourceGroup,
  GoalHelpResponse,
  HistoryRun,
  Locale,
  McpLibraryEntry,
  McpLibraryTestResult,
  MemoryDetail,
  MemoryEntry,
  OfferSelectionRequest,
  OfferSelectionResponse,
  PortalSessionResponse,
  ProjectDetail,
  ProjectSummary,
  RunDetail,
  RunEventEnvelope,
  RunSummary,
  SiteId,
  TemplateDetail,
} from "./types";
import { createWebV5Client } from "./v5-client.js";

/**
 * A refusal the API answered, from its one error body (V6 plan 13.3.1 "The wire" → Errors):
 * `code` and `status`, the body's `data` as `params` (what the error copy interpolates), its
 * `message` as `serverMessage` (the Error's own message stays the code) and its `traceId`, the
 * request id the support copy quotes.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly params?: Record<string, unknown>;
  readonly serverMessage?: string;
  readonly traceId?: string;

  constructor(status: number, body: ApiErrorInit) {
    super(body.code);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.params = body.params;
    this.serverMessage = body.message;
    this.traceId = body.traceId;
  }
}

/**
 * The run a refused start or replay names (decision D73): a 409 idempotency_key_reused whose
 * key's first call started a run carries that run's id as `data.runId`. Null for any other error.
 */
export function startedRunIdOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== IDEMPOTENCY_KEY_REUSED) return null;
  const runId = error.params?.runId;
  return typeof runId === "string" && runId.length > 0 ? runId : null;
}

/**
 * A refusal the packaged client raised (`ApiClientError`, read from the one error body at the
 * answer's status, or `HTTP_<status>` for a body it cannot read, `NETWORK_ERROR` for no answer)
 * as the site's ApiError, so every screen's error copy, its code checks and the trace id it quotes
 * read it exactly as before. The one place the two error types meet: ApiClient's procedures pass
 * through it, and so do the run client's commands and stream (use-run-stream.ts). Anything else
 * (an AbortError, a TypeError) as it is.
 */
export function toApiError(error: unknown): unknown {
  if (!isApiClientError(error)) return error;
  const params =
    error.data && typeof error.data === "object" && !Array.isArray(error.data)
      ? (error.data as Record<string, unknown>)
      : undefined;
  return new ApiError(error.status, {
    code: error.code,
    ...(params ? { params } : {}),
    ...(error.message ? { message: error.message } : {}),
    ...(error.traceId ? { traceId: error.traceId } : {}),
  });
}

/** A call's options carrying its Idempotency-Key, or none when the caller named no key. */
function idempotent(key: string | undefined): { context: ApiClientContext } | undefined {
  return key ? { context: { idempotencyKey: key } } : undefined;
}

/* ---------------- a run's deliverables as explorer entries ---------------- */

const RUN_OUTPUT_KEY = "run-output:";
/** `run-output:<runId>:<path>` — the explorer's opaque key for a deliverable. */
export function runOutputKey(runId: string, path: string): string {
  return `${RUN_OUTPUT_KEY}${runId}:${path}`;
}
export function parseRunOutputKey(entryKey: string): { runId: string; path: string } | null {
  if (!entryKey.startsWith(RUN_OUTPUT_KEY)) return null;
  const rest = entryKey.slice(RUN_OUTPUT_KEY.length);
  const colon = rest.indexOf(":");
  if (colon < 1) return null;
  return { runId: rest.slice(0, colon), path: rest.slice(colon + 1) };
}
/** A stable uuid-shaped id for a deliverable (the explorer's resource id; no provider id is involved). */
function stableFileId(seed: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < seed.length; i++) {
    h1 = Math.imul(h1 ^ seed.charCodeAt(i), 0x01000193) >>> 0;
    h2 = Math.imul(h2 + seed.charCodeAt(i), 0x811c9dc5) >>> 0;
  }
  const hex = (h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0")).repeat(2);
  return `file:${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-8${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
const MEMORY_PREFIX = "memory:";
/** Whether an explorer key names a memory note (`memory:<uuid>`), whose bytes come from getMemory. */
export function isMemoryKey(entryKey: string): boolean {
  return entryKey.startsWith(MEMORY_PREFIX);
}
/** A memory note as an explorer entry: mounted under /mnt/memory on Claude, edited in place, archived rather than deleted. */
export function memoryExplorerEntry(memory: WireMemoryEntry): ExplorerEntry {
  const archived = Boolean(memory.archivedAt);
  const name =
    memory.logicalPath.slice(memory.logicalPath.lastIndexOf("/") + 1) || memory.logicalPath;
  return {
    entryKey: memory.id,
    entryKind: "memory",
    sourceGroup: "memory",
    resource: { kind: "memory", id: memory.id },
    displayName: name,
    displayPath: memory.logicalPath,
    mountPath: archived ? null : memoryMountPath(memory.logicalPath),
    mediaType: /\.(md|markdown)$/i.test(name) ? "text/markdown" : "text/plain",
    sizeBytes: memory.sizeBytes,
    createdAt: memory.createdAt ?? memory.updatedAt,
    modifiedAt: memory.updatedAt,
    lifecycleState: archived ? "archived" : "available",
    origin: memory.updatedByRunId ? "agent" : "user",
    originRunId: memory.updatedByRunId ?? null,
    capabilities: {
      preview: true,
      download: false,
      archive: !archived,
      delete: false,
      editContent: !archived,
      navigateEdit: false,
      restore: archived,
    },
  };
}
/** A deliverable as an explorer entry, so the shared preview can open it like any file. */
export function runOutputEntry(
  runId: string,
  output: { path: string; sizeBytes: number; mediaType: string },
): ExplorerEntry {
  const name = output.path.slice(output.path.lastIndexOf("/") + 1) || output.path;
  const now = new Date().toISOString();
  return {
    entryKey: runOutputKey(runId, output.path),
    entryKind: "user_artifact",
    sourceGroup: "user_artifact",
    resource: { kind: "file", id: stableFileId(`${runId}:${output.path}`) },
    displayName: name,
    displayPath: output.path,
    mountPath: output.path,
    mediaType: output.mediaType,
    sizeBytes: output.sizeBytes,
    createdAt: now,
    modifiedAt: now,
    lifecycleState: "available",
    origin: "agent",
    originRunId: runId,
    capabilities: {
      preview: true,
      download: true,
      delete: false,
      editContent: false,
      navigateEdit: false,
      restore: false,
    },
  };
}

export class ApiClient {
  /**
   * Fired when a product call answers 401 — the session cookie is dead. The app shell
   * invalidates the cached bootstrap so AuthGate lands the user back on sign-in. The sign-in
   * procedures are excluded (v5-client.ts): their 401s are part of signing in, and a bootstrap
   * 401 already renders as signed-out (re-firing on it would refetch-loop).
   */
  onUnauthorized: (() => void) | null = null;

  /**
   * The packaged client (`@mfa/v5-client`, V6 plan H.17 and H.18): every procedure the contract
   * declares, typed, and the hand-written routes it declares (bytes, uploads, the snapshot), over
   * the session cookie, the page's release watch and the dead-session rule (v5-client.ts). Every
   * method below is a call on it; the builder console drives its run through the run client
   * built on it (`openRun`), and the /dev tools read their inspector stream from it.
   */
  readonly v5: V5Client;

  /** The Idempotency-Keys of runs.start and runs.replay: one per distinct input (D73). */
  private readonly startKeys = new RetryKeys();
  private readonly replayKeys = new RetryKeys();

  /**
   * `releases` is the page's release watch (release-watch.ts): every answer's x-v5-release is
   * reported to it, and so is an answer this build cannot decode.
   */
  constructor(
    readonly baseUrl = API_PREFIX,
    releases: ReleaseWatch = releaseWatch,
  ) {
    this.v5 = createWebV5Client({
      baseUrl,
      releases,
      onUnauthorized: () => this.onUnauthorized?.(),
    });
  }

  /**
   * A call on the packaged client with the site's error semantics: a refusal (an ApiClientError,
   * read from the one error body at its status) is the web ApiError carrying the same code,
   * status, params and trace id every screen reads; a cancellation passes as it is. A 401
   * outside the sign-in procedures has fired onUnauthorized already, in the client's fetch.
   */
  private async procedure<T>(run: () => Promise<T>): Promise<T> {
    try {
      return await run();
    } catch (error) {
      throw toApiError(error);
    }
  }

  getBootstrap(): Promise<BootstrapResponse> {
    return this.procedure(() => this.v5.rpc.identity.bootstrap());
  }

  exchangeAuth(idToken: string, locale?: Locale): Promise<BootstrapResponse> {
    return this.procedure(() => this.v5.rpc.identity.exchange({ idToken, locale }));
  }

  async logout(): Promise<void> {
    await this.procedure(() => this.v5.rpc.identity.logout());
  }

  /** Post-password-reset containment: keep this session, revoke every other one. */
  revokeOtherSessions(): Promise<{ revokedSessions: number }> {
    return this.procedure(() => this.v5.rpc.identity.revokeOtherSessions());
  }

  /** Server-vouched Firebase action-link continuation URL; null → use /login. */
  validateContinuation(continueUrl: string): Promise<{ continueUrl: string | null }> {
    return this.procedure(() => this.v5.rpc.identity.validateContinuation({ continueUrl }));
  }

  updateAccountPreferences(locale: Locale): Promise<{ locale: Locale }> {
    return this.procedure(() => this.v5.rpc.identity.updatePreferences({ locale }));
  }

  /** The account's live API keys on this site (apiKeys.list): never a secret, a revoked key or an engine key. */
  listApiKeys(): Promise<{ keys: ApiKey[] }> {
    return this.procedure(() => this.v5.rpc.apiKeys.list());
  }

  /** Issue an API key (apiKeys.create). The answer carries the secret, the one time it is ever sent. */
  createApiKey(input: ApiKeyCreateInput): Promise<{ key: ApiKey; secret: string }> {
    return this.procedure(() =>
      this.v5.rpc.apiKeys.create({
        name: input.name,
        access: input.access,
        ...(input.projectId ? { projectId: input.projectId } : {}),
        ...(input.expiresInDays ? { expiresInDays: input.expiresInDays } : {}),
      }),
    );
  }

  /** Revoke one of the account's API keys (apiKeys.revoke); it fails at its next request. */
  revokeApiKey(id: string): Promise<{ revoked: true }> {
    return this.procedure(() => this.v5.rpc.apiKeys.revoke({ id }));
  }

  listProjects(): Promise<{ projects: ProjectSummary[] }> {
    return this.procedure(() => this.v5.rpc.projects.list({}));
  }

  /**
   * projects.create takes a name and an optional catalog version, nothing else: no share
   * token, no profile, no locale. The input type says exactly that, so a caller cannot hand
   * it a field the server would drop (workflows/V5-Template-Method-Review.md R7).
   */
  createProject(input: { name: string; templateVersionId?: string }): Promise<{
    project: ProjectSummary;
  }> {
    return this.procedure(() =>
      this.v5.rpc.projects.create({
        name: input.name,
        ...(input.templateVersionId ? { templateVersionId: input.templateVersionId } : {}),
      }),
    ).then(({ project }) => ({ project }));
  }

  /**
   * Activation funnel: mark that the guided starter wizard was opened for this
   * subject (null = owner). Audit-only, responds 204; fired fire-and-forget per
   * wizard mount, so the metric that consumes it uses MIN(occurred_at).
   */
  async recordWizardStarted(): Promise<void> {
    // V5 keeps no activation funnel yet.
  }

  /** The project and its current revision exactly as the server answers, for the dev tools. */
  readProject(
    projectId: string,
    signal?: AbortSignal,
  ): Promise<{ project: ProjectDetail; revision: ProjectRevision }> {
    return this.procedure(() =>
      this.v5.rpc.projects.get({ projectId }, { ...(signal ? { signal } : {}) }),
    );
  }

  /**
   * The project and its current revision (projects.get): the raw documents the Builder's codec
   * preview edits and its Code view shows (plan rows H.39, H.51). The client holds nothing
   * between calls: every save answers the revision it wrote, and the page keeps that.
   */
  getProject(projectId: string): Promise<{ project: ProjectDetail; revision: ProjectRevision }> {
    return this.procedure(() => this.v5.rpc.projects.get({ projectId }));
  }

  /**
   * The project's current revision as blocks (projects.blocks): what the Builder's canvas and
   * panels render (design h4 §B.5, §B.10; plan row H.39).
   */
  getBlocks(projectId: string): Promise<{ document: BlockDocument }> {
    return this.procedure(() => this.v5.rpc.projects.blocks({ projectId }));
  }

  /**
   * The block schema (blocks.schema): the kinds, the option lists the forms render from and the
   * support rows that grey an option. Public and cached by its etag: an answer whose etag the
   * client already holds is the held object, so a refetch that changed nothing re-renders nothing.
   */
  private blocksSchema: BlocksSchemaResult | null = null;

  async getBlocksSchema(): Promise<BlocksSchemaResult> {
    const next = await this.procedure(() => this.v5.rpc.blocks.schema({}));
    if (this.blocksSchema && this.blocksSchema.etag === next.etag) return this.blocksSchema;
    this.blocksSchema = next;
    return next;
  }

  /**
   * The Builder's save (projects.applyBlocks): the unsaved edit operations, applied to the
   * current revision through the draft gate, and the new revision read back as blocks.
   */
  applyBlocks(
    projectId: string,
    baseRevision: number,
    ops: readonly BlockOp[],
  ): Promise<{ project: ProjectDetail; revision: ProjectRevision; document: BlockDocument }> {
    return this.procedure(() =>
      this.v5.rpc.projects.applyBlocks({ projectId, baseRevision, ops: [...ops] }),
    );
  }

  /**
   * The Code view's save (projects.saveDraft with the documents as text, design h4 §B.7 "the
   * Code view is the escape hatch"): the applied agent.yaml, the overlay and the sidecar after
   * any block edit made beside it, through the same gate.
   */
  saveDraftDocuments(
    projectId: string,
    baseRevision: number,
    documents: { agentYaml: string; overlayYaml?: string | null; sidecar: ProjectSidecar },
  ): Promise<{ project: ProjectDetail; revision: ProjectRevision }> {
    return this.procedure(() =>
      this.v5.rpc.projects.saveDraft({
        projectId,
        baseRevision,
        agentYaml: documents.agentYaml,
        ...(documents.overlayYaml !== undefined ? { overlayYaml: documents.overlayYaml } : {}),
        sidecar: documents.sidecar,
      }),
    );
  }

  listProjectRuns(projectId: string): Promise<{ runs: RunSummary[] }> {
    return this.procedure(() => this.v5.rpc.runs.listForProject({ projectId }));
  }

  /** One page of the account's runs, newest first; pass `nextCursor` back as `cursor` for the next. */
  listRuns(
    params: { cursor?: string; limit?: number } = {},
  ): Promise<{ runs: HistoryRun[]; nextCursor: string | null }> {
    return this.procedure(() =>
      this.v5.rpc.runs.history({
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
      }),
    );
  }

  getRun(runId: string): Promise<{ run: RunDetail }> {
    return this.procedure(() => this.v5.rpc.runs.get({ runId }));
  }

  /**
   * One page of a run's durable event log after `afterSequence` (up to 500 per page).
   * `nextSequence` is the last sequence the page read — pass it back as `afterSequence`
   * for the next page; null once a page reads nothing. A page can carry fewer events
   * than it read, since rows outside the console's vocabulary are dropped.
   */
  listRunEvents(
    params: { runId: string; afterSequence?: number; limit?: number },
    signal?: AbortSignal,
  ): Promise<{ events: RunEventEnvelope[]; nextSequence: number | null }> {
    return this.procedure(() => this.v5.rpc.runs.events(params, { ...(signal ? { signal } : {}) }));
  }

  /** A run's deliverables (path, size, media type); a run whose session is gone is 409 run_not_live. */
  listRunOutputs(
    runId: string,
    signal?: AbortSignal,
  ): Promise<{ outputs: Array<{ path: string; sizeBytes: number; mediaType: string }> }> {
    return this.procedure(() =>
      this.v5.rpc.runs.outputs({ runId }, { ...(signal ? { signal } : {}) }),
    );
  }

  /**
   * The dev tools' preflight: the checks runs.start makes for this input, without starting a
   * run. Blockers are a normal answer (ready: false); a project the viewer cannot see is 404.
   */
  preflightProject(input: RunStartInput, signal?: AbortSignal): Promise<PreflightResult> {
    return this.procedure(() =>
      this.v5.rpc.devtools.preflightProject(input, { ...(signal ? { signal } : {}) }),
    );
  }

  /**
   * The dev tools' sandbox time: per run on the sandbox path, the awake windows and an Azure
   * estimate at list price, with the runtime the run was charged; and the account's last 24 h.
   * The estimate is never charged.
   */
  sandboxTime(runIds: string[], signal?: AbortSignal): Promise<SandboxTimeResult> {
    return this.procedure(() =>
      this.v5.rpc.devtools.sandboxTime({ runIds }, { ...(signal ? { signal } : {}) }),
    );
  }

  async listExplorer(
    projectId: string,
    params: {
      sourceGroup?: ExplorerSourceGroup;
      q?: string;
      after?: string;
      limit?: number;
      runId?: string;
      includeArchived?: boolean;
    } = {},
  ): Promise<ExplorerListResponse> {
    // Uploads are the project's files and memory its notes (procedures); artifacts are the
    // displayed run's deliverables. The other groups do not exist locally yet, so they list nothing.
    // Every source is listed and counted, whichever is selected: the Files rail shows each
    // source's count beside the selected one. The selected source narrows only the files.
    const uploads = (await this.procedure(() => this.v5.rpc.projects.listFiles({ projectId })))
      .files;
    const outputs = params.runId
      ? (await this.procedure(() => this.v5.rpc.runs.outputs({ runId: params.runId! }))).outputs
      : [];
    const memories = (
      await this.procedure(() =>
        this.v5.rpc.projects.listMemory({
          projectId,
          ...(params.includeArchived ? { includeArchived: true } : {}),
        }),
      )
    ).memories;
    const needle = params.q?.trim().toLowerCase();
    const listed = [
      ...uploads,
      ...outputs.map((output) => runOutputEntry(params.runId!, output)),
      ...memories.map(memoryExplorerEntry),
    ].filter(
      (entry) =>
        !needle ||
        entry.displayName.toLowerCase().includes(needle) ||
        entry.displayPath.toLowerCase().includes(needle),
    );
    const count = (group: string) => listed.filter((entry) => entry.sourceGroup === group).length;
    const files = params.sourceGroup
      ? listed.filter((entry) => entry.sourceGroup === params.sourceGroup)
      : listed;
    return ExplorerListResponseSchema.parse({
      files,
      counts: {
        all: listed.length,
        upload: count("upload"),
        user_artifact: count("user_artifact"),
        skill: 0,
        session_log: 0,
        memory: count("memory"),
        yaml: 0,
      },
      nextCursor: null,
    });
  }
  /** Where an explorer entry's bytes are: a deliverable's run route, or the project's entry route. */
  explorerContentUrl(projectId: string, entryKey: string, mode: FileMode): string {
    const output = parseRunOutputKey(entryKey);
    return output
      ? this.v5.routes.runOutputContent(output.runId, output.path, mode)
      : this.v5.routes.projectEntryContent(projectId, entryKey, mode);
  }

  /** An explorer entry's bytes from that route, through the packaged client (its refusal as ApiError). */
  private explorerContent(
    projectId: string,
    entryKey: string,
    mode: FileMode,
    signal?: AbortSignal,
  ): Promise<Response> {
    const output = parseRunOutputKey(entryKey);
    const options = { mode, ...(signal ? { signal } : {}) };
    return this.procedure(() =>
      output
        ? this.v5.download(output.runId, output.path, options)
        : this.v5.projectEntryContent(projectId, entryKey, options),
    );
  }

  async previewExplorerEntry(
    projectId: string,
    entryKey: string,
    signal?: AbortSignal,
  ): Promise<ExplorerPreview> {
    // A memory note's text comes from its procedure: there is no bytes route for a note.
    if (isMemoryKey(entryKey)) {
      const { memory } = await this.getMemory(projectId, entryKey);
      const contentType = /\.(md|markdown)$/i.test(memory.logicalPath)
        ? "text/markdown"
        : "text/plain";
      return {
        blob: new Blob([memory.content], { type: contentType }),
        contentType,
        truncated: false,
      };
    }
    const response = await this.explorerContent(projectId, entryKey, "preview", signal);
    return {
      blob: await response.blob(),
      contentType:
        response.headers.get("Content-Type")?.split(";", 1)[0] ?? "application/octet-stream",
      truncated:
        response.headers.get("X-Preview-Truncated") === "true" ||
        response.headers.get("X-Content-Truncated") === "true" ||
        response.headers.get("X-Explorer-Truncated") === "true",
    };
  }

  async readExplorerEntry(
    projectId: string,
    entryKey: string,
    signal?: AbortSignal,
  ): Promise<Blob> {
    const response = await this.explorerContent(projectId, entryKey, "download", signal);
    return response.blob();
  }

  /**
   * One file into the project (the packaged client's `upload`: the declared multipart route).
   * `idempotencyKey` identifies the upload ATTEMPT, not the HTTP request: a retry of the same
   * attempt must reuse it so the server replays the one durable upload intent instead of storing
   * the bytes twice. `mode` is what a name the project already holds does: a `create` (the
   * default) is refused 409 file_exists and writes nothing, a `replace` replaces that file.
   * `targetRunId` is the composer's intent, kept for its callers: V5 attaches no file to a running
   * session (an upload is a project file, mounted at the next run's start) and the server reads no
   * target run, so nothing is sent for it.
   */
  async uploadExplorerFile(
    projectId: string,
    file: File,
    destination: "upload" | "user_artifact",
    options: { targetRunId?: string; idempotencyKey?: string; mode?: ProjectFileSaveMode } = {},
  ): Promise<ExplorerUploadResponse> {
    const response = await this.procedure(() =>
      this.v5.upload(projectId, file, {
        fileName: file.name,
        destination,
        mode: options.mode ?? "create",
        ...(options.idempotencyKey ? { idempotencyKey: options.idempotencyKey } : {}),
      }),
    );
    return ExplorerUploadResponseSchema.parse(response);
  }

  /**
   * Run-exact attachment inventory; `fileId` is always a canonical `file:` id. V5 attaches no
   * file to a running session (an upload is a project file, mounted at the next run's start),
   * so a run's inventory is empty.
   */
  async getRunAttachments(runId: string): Promise<RunAttachmentsResponse> {
    void runId;
    return RunAttachmentsResponseSchema.parse({ files: [] });
  }

  /**
   * Retrying an attachment, like detaching one below, has no server route in V5, since nothing
   * is attached: both refuse without a request, with the unknown-route 404 the server answered
   * them with, so the composer and Files keep the chip as it was (use-run-attachments.ts).
   */
  async retryRunAttachment(runId: string, fileId: string): Promise<RunAttachmentsResponse> {
    void runId;
    void fileId;
    throw new ApiError(404, { code: "not_found" });
  }

  /** Detach from THIS run's session only — the project file stays in Files. */
  async detachRunAttachment(runId: string, fileId: string): Promise<RunAttachmentsResponse> {
    void runId;
    void fileId;
    throw new ApiError(404, { code: "not_found" });
  }

  async deleteExplorerFile(projectId: string, fileId: string): Promise<void> {
    await this.procedure(() => this.v5.rpc.projects.deleteFile({ projectId, fileId }));
  }

  listMemory(
    projectId: string,
    options: { includeArchived?: boolean } = {},
  ): Promise<{ memories: MemoryEntry[] }> {
    return this.procedure(() =>
      this.v5.rpc.projects.listMemory({
        projectId,
        ...(options.includeArchived ? { includeArchived: true } : {}),
      }),
    );
  }

  createMemory(
    projectId: string,
    input: { path: string; content: string },
  ): Promise<{ memory: MemoryEntry }> {
    return this.procedure(() => this.v5.rpc.projects.createMemory({ projectId, ...input }));
  }

  getMemory(projectId: string, memoryId: string): Promise<{ memory: MemoryDetail }> {
    return this.procedure(() => this.v5.rpc.projects.getMemory({ projectId, memoryId }));
  }

  updateMemory(
    projectId: string,
    memoryId: string,
    input: { content: string; expectedSha256?: string },
  ): Promise<{ memory: MemoryEntry }> {
    return this.procedure(() =>
      this.v5.rpc.projects.updateMemory({ projectId, memoryId, ...input }),
    );
  }

  archiveMemory(
    projectId: string,
    memoryId: string,
  ): Promise<{ memory: Pick<MemoryEntry, "id" | "archivedAt"> }> {
    return this.procedure(() => this.v5.rpc.projects.archiveMemory({ projectId, memoryId }));
  }

  restoreMemory(
    projectId: string,
    memoryId: string,
  ): Promise<{ memory: Pick<MemoryEntry, "id" | "archivedAt"> }> {
    return this.procedure(() => this.v5.rpc.projects.restoreMemory({ projectId, memoryId }));
  }

  /** Public billing catalog for this site — works signed out. */
  getBillingCatalog(locale: Locale): Promise<{ catalog: BillingCatalog }> {
    return this.procedure(() => this.v5.rpc.billing.catalog({ locale })).then((catalog) => ({
      catalog,
    }));
  }

  /**
   * The whole platform catalog in one GET (the packaged client's `snapshot`, the declared
   * catalogSnapshot route): every live template of both sites and the vocabulary, in the locale.
   * The route is public and the client sends it without the cookie; the server answers with an
   * ETag and public caching, so the browser's HTTP cache revalidates it with a 304 underneath the
   * one fetch (the client asks for no 304 itself); the gallery filters, counts and orders it in
   * memory.
   */
  async getCatalogSnapshot(locale: Locale): Promise<CatalogSnapshot> {
    const result = await this.procedure(() => this.v5.snapshot(locale));
    if (result.notModified) throw new ApiError(304, { code: "HTTP_304" });
    return CatalogSnapshotSchema.parse(result.snapshot);
  }

  /** Canonical template detail: the prompt-free catalog row, signed out or in. */
  getCatalogTemplate(
    libraryKey: string,
    slug: string,
    locale: Locale,
  ): Promise<{ template: TemplateDetail }> {
    return this.procedure(() => this.v5.rpc.catalog.detail({ libraryKey, slug, locale }));
  }

  getCatalogFacets(locale: Locale): Promise<CatalogFacets> {
    return this.procedure(() => this.v5.rpc.catalog.facets({ locale }));
  }

  /**
   * The team block's member picker (design h4 §B.7.3, plan row H.41): the single-agent templates
   * live on the project's site, from catalog.list (the shelf rules the gallery runs), narrowed by
   * a title search and ordered by title. A member is `<libraryKey>/<slug>`; the picker never
   * writes a version, since the draft gate pins members at save.
   */
  listCatalogSingles(input: {
    from: SiteId;
    q?: string;
    locale?: Locale;
    limit?: number;
  }): Promise<{ templates: CatalogTemplate[]; nextCursor: string | null; total: number }> {
    const q = input.q?.trim();
    return this.procedure(() =>
      this.v5.rpc.catalog.list({
        ...(input.locale ? { locale: input.locale } : {}),
        filters: { kind: "single", from: input.from, ...(q ? { q } : {}) },
        sort: "title",
        limit: input.limit ?? 50,
      }),
    );
  }

  getTemplateYaml(templateVersionId: string): Promise<{ templateVersionId: string; yaml: string }> {
    return this.procedure(() => this.v5.rpc.catalog.yaml({ templateVersionId }));
  }

  goalHelp(
    projectId: string,
    input: { idea: string; graderIds: string[] },
  ): Promise<GoalHelpResponse> {
    return this.procedure(() => this.v5.rpc.projects.goalHelp({ projectId, ...input }));
  }

  /** The Builder's tool-server library (mcpLibrary.list): inactive entries too. */
  listMcpLibrary(): Promise<{ entries: McpLibraryEntry[] }> {
    return this.procedure(() => this.v5.rpc.mcpLibrary.list());
  }

  /** Probe one library entry live (mcpLibrary.test): a friendly ok or failure, never the server's words. */
  testMcpLibraryEntry(entryId: string): Promise<McpLibraryTestResult> {
    return this.procedure(() => this.v5.rpc.mcpLibrary.test({ entryId }));
  }

  /** The ONE authed billing snapshot every billing surface renders from (billing.account). */
  getBillingAccount(): Promise<{ account: BillingAccountSnapshot }> {
    return this.procedure(() => this.v5.rpc.billing.account({})).then((account) => ({ account }));
  }

  /**
   * Where one selection stands (billing.selection), by the selectionId a checkout or portal
   * return carries. A read: it never repeats the selection.
   */
  getBillingSelection(selectionId: string): Promise<BillingSelectionStatus> {
    return this.procedure(() => this.v5.rpc.billing.selection({ selectionId }));
  }

  /**
   * Durable offer selection (billing.selectOffer). `requestId` is the REQUIRED
   * client-generated UUID that identifies the selection idempotently and rides the
   * Idempotency-Key header too; the response's selectionId is the correlation id the
   * return-URL polling converges on.
   */
  selectOffer(input: OfferSelectionRequest): Promise<OfferSelectionResponse> {
    return this.procedure(() =>
      this.v5.rpc.billing.selectOffer(input, { context: { idempotencyKey: input.requestId } }),
    );
  }

  /** Cancel a scheduled (not yet effective) subscription change (billing.cancelChange). */
  cancelSubscriptionChange(changeId: string): Promise<{ canceled: true }> {
    return this.procedure(() => this.v5.rpc.billing.cancelChange({ changeId }));
  }

  /** Provider billing portal (payment method, invoices) — opaque URL only. */
  createPortalSession(): Promise<PortalSessionResponse> {
    return this.procedure(() => this.v5.rpc.billing.portalSession({}));
  }

  /** The admin dashboard (admin.overview) — the server re-authorizes every call. */
  getAdminOverview(): Promise<AdminDashboard> {
    return this.procedure(() => this.v5.rpc.admin.overview());
  }

  /** Put the signed-in admin's OWN account on a plan to test it (admin.switchPlan). */
  adminSwitchPlan(planId: PlanId): Promise<AdminPlanSwitchResult> {
    return this.procedure(() => this.v5.rpc.admin.switchPlan({ planId }));
  }

  /**
   * The Builder's preflight (runs.preflight): what would refuse a run of the project's saved
   * revision, the checker's verdict, what the run needs and the cap's bounds. Blockers are an
   * answer (ready: false), not an error; nothing starts and nothing is spent.
   */
  preflightRun(input: RunStartInput, signal?: AbortSignal): Promise<RunPreflight> {
    return this.procedure(() =>
      this.v5.rpc.runs.preflight(input, { ...(signal ? { signal } : {}) }),
    );
  }

  /**
   * The Builder's check of its unsaved edits (projects.validate): the saved revision with the
   * pending block operations (or the Code view's documents) applied, checked on every provider
   * and runner this deployment serves, each finding naming its block. Nothing is written, spent
   * or called; a refusal of the documents is an answer, not an error.
   */
  validateProject(input: ProjectValidateInput, signal?: AbortSignal): Promise<ProjectValidation> {
    return this.procedure(() =>
      this.v5.rpc.projects.validate(input, { ...(signal ? { signal } : {}) }),
    );
  }

  /**
   * runs.start. V5 composes from the project's current revision; the draft was saved just before.
   * The revision the page showed (or just saved) goes as baseRevision (V6 plan H.43): when another
   * save landed in between, the start is 409 draft_conflict and nothing starts, as a stale save is.
   * Its Idempotency-Key is the start input's (D73): the same input again (a double click, a retry
   * after a lost answer) carries the same key and gets the same run back instead of starting a
   * second one; a changed input (another revision, an edited mission) gets a fresh key, and so
   * does the next start once this one succeeded.
   */
  async startRun(input: StartRunCommand): Promise<{ run: RunSummary }> {
    const key = this.startKeys.keyFor(input);
    const started = await this.procedure(() =>
      this.v5.rpc.runs.start(
        { projectId: input.projectId, baseRevision: input.draftRevision, mission: input.mission },
        idempotent(key),
      ),
    );
    this.startKeys.succeeded(key);
    return started;
  }

  /**
   * The follow-up Send on a finished run whose record names no runner: a NEW run on the same
   * project whose conversation is seeded from the finished run's kept transcript, with the
   * message as its first turn (runs.replay). Its
   * Idempotency-Key is the replay input's, as startRun's is (D73): a retried Send of the same
   * message starts one run. A run's commands (its follow-up in place included) go out through
   * the run client (use-run-stream.ts), never through here.
   */
  async resumeRun(runId: string, message: string): Promise<RunSummary> {
    const input = { runId, message };
    const key = this.replayKeys.keyFor(input);
    const response = await this.procedure(() => this.v5.rpc.runs.replay(input, idempotent(key)));
    this.replayKeys.succeeded(key);
    return response.run;
  }

  /** Projects stay pinned to the version they came from; the banner asks whether a newer one exists. */
  getTemplateUpgrade(projectId: string) {
    return this.procedure(() => this.v5.rpc.projects.templateUpgrade({ projectId }));
  }
  previewTemplateUpgrade(projectId: string, targetVersionId: string) {
    return this.procedure(() =>
      this.v5.rpc.projects.templateUpgradePreview({ projectId, targetVersionId }),
    );
  }
  applyTemplateUpgrade(
    projectId: string,
    input: {
      targetVersionId: string;
      baseRevision: number;
      choices: Record<string, "current" | "target">;
    },
  ) {
    return this.procedure(() => this.v5.rpc.projects.templateUpgradeApply({ projectId, ...input }));
  }
}
